// Franklin brand colors used for auto-assigned avatar fills.
//
// Ordered vivid-first; #ffffff is intentionally excluded (invisible as a fill).
// This list MUST stay in sync with the `v_palette` array in the migration
// supabase/migrations/20260610084522_add_user_profiles_color.sql — the DB seeds
// a user's color from the same ordered palette on first org join.
export const BRAND_AVATAR_COLORS = [
  "#ef4129",
  "#5769bd",
  "#009e46",
  "#ffd900",
  "#a5815d",
  "#696b6c",
  "#808080",
  "#262626",
  "#cacbce",
] as const;

// Picks a readable text color (near-black vs white) for initials drawn on top of
// a solid `hex` fill, using the WCAG relative-luminance formula.
export function getContrastText(hex: string): string {
  const normalized = normalizeHex(hex);
  if (!normalized) return "#ffffff";
  const n = normalized.slice(1);
  const toLinear = (channel: number) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const r = toLinear(parseInt(n.slice(0, 2), 16));
  const g = toLinear(parseInt(n.slice(2, 4), 16));
  const b = toLinear(parseInt(n.slice(4, 6), 16));
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.5 ? "#262626" : "#ffffff";
}

const HEX_PATTERN = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Returns a lowercase 6-digit `#rrggbb` string, or null if the input isn't a
// valid 3- or 6-digit hex color. Expands 3-digit shorthand (#abc -> #aabbcc).
export function normalizeHex(input: string): string | null {
  const trimmed = input.trim();
  if (!HEX_PATTERN.test(trimmed)) return null;
  let hex = trimmed.replace("#", "").toLowerCase();
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return `#${hex}`;
}

export function isValidHex(input: string): boolean {
  return normalizeHex(input) !== null;
}
